import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useRoutes, type Location } from 'react-router-dom';
import gsap from 'gsap';
import pageRoutes, { SITE_PATHS } from '@/router/pageRoutes';
import Header from './Header';
import Footer from './Footer';

const EXIT_MS = 180;
const ENTER_S = 0.33;

// Scroll positions per history entry, for Back / Forward restoration
const savedScroll = new Map<string, number>();

/**
 * Runs before Header's layout effect (rendered as an earlier sibling) so the
 * header measures the new page at its correct scroll position.
 */
function ScrollManager({ locKey, isPop }: { locKey: string; isPop: boolean }) {
  useLayoutEffect(() => {
    const target = isPop ? savedScroll.get(locKey) ?? 0 : 0;
    window.scrollTo(0, target);
    if (!target) return undefined;
    // Page effects (pin spacers, ScrollTriggers) add height after this commit;
    // re-apply once they exist so the restore isn't clamped.
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => window.scrollTo(0, target));
    });
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, [locKey, isPop]);
  return null;
}

export default function TransitionLayout() {
  const location = useLocation();
  const navType = useNavigationType();
  const [shown, setShown] = useState<{ loc: Location; pop: boolean }>({ loc: location, pop: false });
  const contentRef = useRef<HTMLDivElement>(null);
  const latest = useRef({ loc: location, pop: false });
  const timer = useRef<number>(0);
  const swapped = useRef(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Location changed: fade current content out, then swap (timer-driven, so
  // navigation always completes even if the tween never finishes).
  useEffect(() => {
    latest.current = { loc: location, pop: navType === 'POP' };
    if (location.key === shown.loc.key) return;
    if (location.pathname === shown.loc.pathname && location.search === shown.loc.search) {
      setShown(latest.current); // hash-only change: no transition
      return;
    }
    if (timer.current) return; // exit already running; it will pick up the latest location
    savedScroll.set(shown.loc.key, window.scrollY);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const el = contentRef.current;
    if (el && !reduce) {
      gsap.killTweensOf(el);
      gsap.to(el, { opacity: 0, duration: EXIT_MS / 1000, ease: 'power2.in' });
    }
    timer.current = window.setTimeout(() => {
      timer.current = 0;
      swapped.current = true;
      setShown(latest.current);
    }, reduce ? 0 : EXIT_MS);
  }, [location, navType, shown.loc]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // After the new page commits: fade in, and move focus off a removed element
  useEffect(() => {
    if (!swapped.current) return;
    swapped.current = false;
    const el = contentRef.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gsap.killTweensOf(el);
    if (reduce) gsap.set(el, { clearProps: 'opacity' });
    else gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: ENTER_S, ease: 'power2.out', clearProps: 'opacity' });
    const active = document.activeElement;
    if (!active || active === document.body || !document.contains(active)) {
      el.querySelector<HTMLElement>('main')?.focus({ preventScroll: true });
    }
  }, [shown]);

  const page = useRoutes(pageRoutes, shown.loc);
  const isSite = SITE_PATHS.includes(shown.loc.pathname);

  return (
    <div className="min-h-screen flex flex-col bg-background-50 text-foreground-950">
      <ScrollManager locKey={shown.loc.key} isPop={shown.pop} />
      {isSite && <Header pathname={shown.loc.pathname} />}
      <div ref={contentRef} className="flex-1 flex flex-col">
        {page}
        {isSite && <Footer />}
      </div>
    </div>
  );
}